import React, { useState } from 'react';
import startOfTomorrow from 'date-fns/startOfTomorrow';
import {
  Typography,
  Switch,
  Drawer,
  SvgIconButton,
  Box,
  MuiBox,
  FlexBox,
  Card,
  Grid,
  MuiTable,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { actions as kiosksActions, Kiosk, selectors as kiosksSelectors } from 'store/kiosks';
import { KioskHardware } from 'store/kioskHardware';
import { selectors as authSelectors } from 'store/auth';
import { orDash, formatHardwareTitle } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { useStyles } from '../styled';

export interface KioskHardwareStatusProps {
  isOpen: boolean;
  onClose: () => void;
  kiosk: Kiosk;
}

export const KioskHardwareStatus = (props: KioskHardwareStatusProps) => {
  const { isOpen, onClose, kiosk } = props;
  const { updateKiosk } = useActions({
    updateKiosk: kiosksActions.updateKiosk
  });
  const kioskData = useSelector(kiosksSelectors.selectKiosk);
  const admin = useSelector(authSelectors.selectAdmin);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [kioskStatus, setKioskStatus] = useState<string>(kiosk.status);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);

  // check for user is Read-Only Admin or not, if yes hide switch button and show text
  React.useEffect(() => {
    if (admin && admin.role === 'ReadOnlyAdmin') setReadOnlyAdmin(true);
  }, [admin]);

  const { changeStatusKiosk } = useActions({ changeStatusKiosk: kiosksActions.changeStatusKiosk });

  const onSwitchStatus = (checked: boolean) => {
    setShowConfirmation(true);

    const status = checked ? 'Active' : 'Inactive';
    // TODO: log event
    changeStatusKiosk({ kioskId: kiosk.id, status, adminId: admin ? admin.id : '' });
    setKioskStatus(status);

    updateKiosk({
      id: kiosk.id,
      status,
      unscheduledUntil: startOfTomorrow()
    });
  };

  const HardwareStatus = (Status: { type: string }) => {
    const status = Object.values(Status).toString();
    if (status === 'Inactive') {
      return <SvgIcon name="error_circle" color="#C40B0F" />;
    } else if (status === 'Active') {
      return <SvgIcon name="success_circle" color="#64AF15" />;
    }
    return <Typography>--</Typography>;
  };

  const closeConfirmOverlay = () => {
    setShowConfirmation(false);
  };

  const getHardwareDisplayName = (kioskHardware: KioskHardware) => {
    const { type, configuration } = kioskHardware;

    if (type === 'printer' && configuration.displayName) {
      return `${configuration.displayName} ${formatHardwareTitle(type)}`;
    }

    return formatHardwareTitle(type);
  };

  const classes = useStyles({ showDrawer: isOpen })();

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <MuiBox className={classes.kioskStatusChangeTitle}>
              <Typography className={classes.kioskStatusTitle} sx={{ fontWeight: 600 }}>
                {kiosk.name} is now {kioskStatus}
              </Typography>
            </MuiBox>
          }
          description={
            <MuiBox className={classes.kioskStatusChangeDescription}>
              <Typography className={classes.kioskStatusDescription}>
                {kiosk.name} will remain {kioskStatus} until it resumes its scheduled operation tomorrow.
              </Typography>
            </MuiBox>
          }
          confirmText="Ok"
          onConfirm={closeConfirmOverlay}
        />
      )}
      <Drawer open={isOpen} onClose={onClose} hasBorderRadius>
        <Grid container direction="column" className={classes.kioskDetailsOverlay}>
          <Card fullHeight>
            <Grid className={classes.kioskOverlayHeaderBlock}>
              <Grid className={classes.kioskOverlayHeading}>
                <FlexBox>
                  <Typography className={classes.kioskStatusHeading} sx={{ fontWeight: 600 }}>
                    Kiosk Status
                  </Typography>
                </FlexBox>
                <FlexBox>
                  <Switch
                    checked={kiosk.status === 'Active'}
                    onChange={(_e, checked) => onSwitchStatus(checked)}
                    data-testid="kiosk-status-switch"
                    checkedLabel="ACTIVE"
                    uncheckedLabel="INACTIVE"
                    disabled={!readOnlyAdmin ? false : true}
                  />
                </FlexBox>
              </Grid>
              <Grid className={classes.kioskOverlayCloseIcon}>
                <SvgIconButton appearance="transparent" onClick={onClose} data-testid="kiosk-status-x">
                  <SvgIcon name="exit_circle" size={40} />
                </SvgIconButton>
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography fontWeight={600} fontSize={16}>
                {kiosk.name}
              </Typography>
            </Box>
            <MuiBox className={classes.kioskTableBox}>
              <MuiTable.TableContainer id="kioskHardwareTable">
                <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
                  <MuiTable.TableHead sx={{ background: 'white' }}>
                    <MuiTable.TableRow>
                      <MuiTable.TableCell className={classes.kioskHardwareHeaderType} sx={{ width: '250px' }}>
                        Type
                      </MuiTable.TableCell>
                      <MuiTable.TableCell className={classes.kioskHardwareHeaders} sx={{ width: '250px' }}>
                        Serial Number
                      </MuiTable.TableCell>
                      <MuiTable.TableCell className={classes.kioskHardwareHeaders} sx={{ width: '150px' }}>
                        Status
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  </MuiTable.TableHead>
                  <MuiTable.TableBody>
                    {((kioskData && kioskData.hardware) || []).map((kioskHardwareObj: KioskHardware) => {
                      return (
                        <MuiTable.TableRow className={classes.kioskHardwareTableRows}>
                          <MuiTable.TableCell className={classes.kioskHardwareType}>
                            <Typography fontSize={16} fontWeight={600}>
                              {getHardwareDisplayName(kioskHardwareObj)}
                            </Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell>
                            <Typography fontSize={16}>{orDash(kioskHardwareObj.serialCode)}</Typography>
                          </MuiTable.TableCell>
                          <MuiTable.TableCell>
                            <HardwareStatus type={kioskHardwareObj.status} />
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      );
                    })}
                  </MuiTable.TableBody>
                </MuiTable.Table>
              </MuiTable.TableContainer>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};

export default KioskHardwareStatus;
